import { Box, Typography, Button, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { DateUtils } from '../../../../../common/utils';
import { appleTvProductId, availableAddonStates, getProductName } from '../../../constants';

const useStyles = makeStyles(theme => ({
	container: {
		display: 'flex',
		flexDirection: 'column',
		padding: theme.spacing(3),
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.spacing(0.5),
		border: `1px solid ${theme.palette.divider}`,
		width: '653px',
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: theme.spacing(2),
	},
	title: {
		fontWeight: theme.typography.fontWeightBold,
	},
	imagePlaceholder: ({ productId }: { productId: number }) => ({
		width: 56,
		height: 56,
		borderRadius: theme.spacing(1),
		flexShrink: 0,
		background: `url(/${productId}.webp) center center / contain no-repeat`,
	}),
	content: {
		display: 'flex',
		justifyContent: 'space-between',
		gap: theme.spacing(3),
		marginBottom: theme.spacing(2),
		[theme.breakpoints.down('sm')]: {
			flexDirection: 'column',
		},
	},
	column: {
		flex: 1,
		minWidth: 0,
	},
	label: {
		color: theme.palette.text.secondary,
		fontWeight: theme.typography.fontWeightBold,
	},
	value: {
		wordBreak: 'break-word',
	},
	statusChangedAt: {
		marginTop: theme.spacing(2),
	},
	footer: {
		marginTop: theme.spacing(1),
	},
}));

function getExternalUserIdForProduct(productId: number, externalId: string, externalUserId: string): string {
	if (productId === appleTvProductId) {
		// For Apple TV add-on, the externalId is the Apple TV User ID
		return externalId;
	}
	// For Disney+ add-on, the externalUserId is the Disney+ User ID
	return externalUserId;
}

interface SubscriptionAddonInfoProps {
	productId: number;
	status: string;
	statusChangedAt?: string;
	externalId: string;
	externalUserId: string;
	onActivate: () => void;
	onUnlink?: () => void;
}

function SubscriptionAddonInfo({ productId, status, statusChangedAt, externalId, externalUserId, onActivate, onUnlink }: SubscriptionAddonInfoProps) {
	const { container, header, title, imagePlaceholder, content, column, label, value, statusChangedAt: statusChangedAtClass, footer } = useStyles({ productId });
	const [t] = useTranslation();
	const productName = getProductName(productId);
	const buttonDisabled = status !== availableAddonStates.pendingActivation;
	const userId = getExternalUserIdForProduct(productId, externalId, externalUserId);

	return (
		<Box className={container}>
			<Box className={header}>
				<Typography variant="h5" className={title}>
					{productName}
				</Typography>
				<Box className={imagePlaceholder} />

			</Box>
			<Box className={content}>
				<Box className={column}>
					<Typography variant="body1" className={label}>
						{t('subscription.subscriptionAddonAdminStatus')}:
					</Typography>
					<Typography variant="h6" className={value}>
						{status}
					</Typography>
					{statusChangedAt ? (
						<Box className={statusChangedAtClass}>
							<Typography variant="body1" className={label}>
								{t('subscription.subscriptionAddonAdminStatusChanged')}:
							</Typography>
							<Typography variant="body2" className={value}>
								{DateUtils.getDateTime(statusChangedAt)}
							</Typography>
						</Box>
					) : null}
				</Box>
				<Box className={column}>
					<Typography variant="body1" className={label}>
						{productName} ID:
					</Typography>
					<Typography variant="body2" className={value}>
						{userId}
					</Typography>
				</Box>
			</Box>
			<Box className={footer}>
				{status === availableAddonStates.activated && onUnlink ? (
					<Button variant="outlined" onClick={onUnlink}>
						{t('subscription.subscriptionUnlinkAddon')}
					</Button>
				) : (
					<Button variant="contained" color="primary" onClick={onActivate} disabled={buttonDisabled}>
						{t('subscription.subscriptionResendAddonActivationLink')}
					</Button>
				)}
			</Box>
		</Box>
	);
}

export default SubscriptionAddonInfo;
